import { useState, useRef } from 'react';
import { Avatar, Box, IconButton, CircularProgress, Typography } from '@mui/material';
import { CameraAlt, Delete } from '@mui/icons-material';
import httpClient from '../../utils/httpClient';
import { colors, designTokens } from '../../theme/theme';

// DS-prescribed 44×44pt tap-target floor for the camera/remove buttons,
// even when the host avatar is small.
const MIN_TAP_TARGET = 44;

interface Props {
  currentUrl?: string | null;
  firstName?: string;
  lastName?: string;
  size?: number;
  onUploadSuccess?: (url: string) => void;
  onRemoveSuccess?: () => void;
}

export default function AvatarUpload({
  currentUrl, firstName = '', lastName = '', size = 100,
  onUploadSuccess, onRemoveSuccess,
}: Props) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const displayUrl = previewUrl || currentUrl;
  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum 5MB.'); return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Please upload a JPEG, PNG, or WebP image.'); return;
    }

    // Show preview immediately
    setPreviewUrl(URL.createObjectURL(file));
    setError('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      // HttpClient auto-detects FormData and omits Content-Type so the browser
      // can set the correct multipart boundary — no extra config needed.
      const { data } = await httpClient.post('/users/me/avatar', formData);
      setPreviewUrl(data.avatar_url);
      onUploadSuccess?.(data.avatar_url);
    } catch (err: any) {
      console.warn('[AvatarUpload] upload failed', err);
      setError(err.message || 'Upload failed');
      setPreviewUrl(null);
    } finally {
      setUploading(false);
      // Reset input so same file can be re-selected
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setUploading(true);
    try {
      await httpClient.del('/users/me/avatar');
      setPreviewUrl(null);
      onRemoveSuccess?.();
    } catch (err: any) {
      console.warn('[AvatarUpload] remove failed', err);
      setError(err.message || 'Failed to remove');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar
          src={displayUrl || undefined}
          sx={{
            width: size, height: size,
            bgcolor: colors.primary.main,
            fontSize: size * 0.35,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'opacity 0.2s',
            '&:hover': { opacity: 0.8 },
          }}
          onClick={() => !uploading && inputRef.current?.click()}
        >
          {uploading ? <CircularProgress size={size * 0.35} sx={{ color: 'white' }} /> : initials}
        </Avatar>

        {/* Camera overlay — coral gradient + glow per DS Brand Mark; 44×44 min tap target. */}
        <IconButton
          aria-label="Upload new profile photo"
          onClick={() => !uploading && inputRef.current?.click()}
          sx={{
            position: 'absolute', bottom: 0, right: 0,
            width: Math.max(size * 0.32, MIN_TAP_TARGET),
            height: Math.max(size * 0.32, MIN_TAP_TARGET),
            background: colors.primary.gradient,
            color: '#FFFFFF',
            border: `2px solid ${colors.background.default}`,
            boxShadow: designTokens.shadow.glow,
            transition: `all ${designTokens.transition.normal}`,
            '&:hover': {
              background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
              boxShadow: designTokens.shadow.glowStrong,
              transform: 'scale(1.05)',
            },
          }}
        >
          <CameraAlt sx={{ fontSize: Math.max(size * 0.18, 20), color: '#FFFFFF' }} />
        </IconButton>

        {/* Remove button (only when avatar exists) — glass surface + error-tint border; 44×44 tap target. */}
        {displayUrl && !uploading && (
          <IconButton
            aria-label="Remove profile photo"
            onClick={handleRemove}
            sx={{
              position: 'absolute', top: 0, right: 0,
              width: Math.max(size * 0.25, MIN_TAP_TARGET),
              height: Math.max(size * 0.25, MIN_TAP_TARGET),
              bgcolor: colors.error.subtle,
              border: `1px solid ${designTokens.glass.borderHover}`,
              color: colors.error.main,
              transition: `all ${designTokens.transition.normal}`,
              '&:hover': {
                bgcolor: colors.error.subtle,
                borderColor: colors.error.main,
                transform: 'scale(1.05)',
              },
            }}
          >
            <Delete sx={{ fontSize: Math.max(size * 0.15, 18), color: colors.error.main }} />
          </IconButton>
        )}
      </Box>

      {error && (
        <Typography variant="caption" sx={{ color: colors.error.main, textAlign: 'center' }}>
          {error}
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
    </Box>
  );
}
